import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    Renderer2,
    ViewChild,
    forwardRef,
    inject,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateTime } from 'luxon';
import { formatDate } from 'ngx-bootstrap/chronos';
import {
    BsDatepickerConfig,
    BsLocaleService,
    BsDatepickerInputDirective,
    BsDatepickerDirective,
} from 'ngx-bootstrap/datepicker';
import { AbstractNgModelComponent } from '../../ng-model.component';
import { DateTimeService } from '../date-time.service';
import compare from 'just-compare';
import { NgxBootstrapDatePickerConfigService } from 'assets/ngx-bootstrap/ngx-bootstrap-datepicker-config.service';

@Component({
    selector: 'abp-datetime-picker',
    templateUrl: './abp-datetime-picker.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AbpDateTimePickerComponent),
            multi: true,
        },
        { provide: DateTimeService, useClass: DateTimeService },
    ],
    imports: [BsDatepickerInputDirective, BsDatepickerDirective],
})
export class AbpDateTimePickerComponent extends AbstractNgModelComponent {
    @ViewChild('datePicker', { static: true }) datePicker: ElementRef;
    @Input() bsConfig: BsDatepickerConfig = null;
    @Input() isUtc = true;
    @Input() placeholder: string = '';
    @Input() disabled: boolean = false;

    date: DateTime | null | undefined;
    lastDate: Date = null;

    private _dateTimeService = inject(DateTimeService);
    private _renderer = inject(Renderer2);
    private _localeService = inject(BsLocaleService);
    private _cdr = inject(ChangeDetectorRef);

    // FIXED METHOD — NO DATE SHIFT ANYMORE
    setDate(date: Date) {
        if (!date) {
            this.writeValue(null);
        } else if (date instanceof Date && !compare(this.lastDate, date) && date.toString() !== 'Invalid Date') {

            date = this.clearTime(date);

            // FIXED UTC HANDLING — NO TIMEZONE OFFSET SUBTRACTION
            if (this.isUtc) {
                // Directly convert to ISO without subtracting timezone offset
                let utcDateString = date.toISOString();
                date = this._dateTimeService.fromISODateString(utcDateString).toJSDate();
            } else if (abp.clock.provider.supportsMultipleTimezone) {
                date = this._dateTimeService.changeTimeZone(
                    date,
                    abp.timing.timeZoneInfo.iana.timeZoneId
                );
            }

            this.writeValue(this._dateTimeService.fromJSDate(date));
        }
    }

    writeValue(value: any) {
        if (!value) {
            this.cleanValue();
            return;
        }

        // Handle different input types
        let dateTimeValue: DateTime;
        let jsDateValue: Date;

        if (DateTime.isDateTime(value)) {
            dateTimeValue = value;
            jsDateValue = value.toJSDate();
        } else if (value instanceof Date) {
            dateTimeValue = DateTime.fromJSDate(value);
            jsDateValue = value;
        } else if (typeof value === 'string') {
            const parsed = DateTime.fromISO(value);
            if (parsed.isValid) {
                dateTimeValue = parsed;
                jsDateValue = parsed.toJSDate();
            } else {
                this.cleanValue();
                return;
            }
        } else {
            this.cleanValue();
            return;
        }

        this.date = dateTimeValue;
        this.lastDate = jsDateValue;
        super.writeValue(dateTimeValue);
        this.change();
        this._cdr.markForCheck();
    }

    change() {
        if (!this.date) {
            return;
        }

        this.value = this.date;
        this.setInputValue(this.lastDate);
    }

    setInputValue(value?: Date): void {
        const initialDate = !value
            ? ''
            : formatDate(value, this.getConfig().dateInputFormat, this._localeService.currentLocale);

        const inputEl = this.datePicker.nativeElement;
        this._renderer.setProperty(inputEl, 'value', initialDate);
        this._renderer.setAttribute(inputEl, 'data-value', initialDate);
    }

    cleanValue() {
        this.date = null;
        this.value = null;
        this.lastDate = null;
        this._cdr.markForCheck();
    }

    clearTime(date: Date): Date {
        if (!this.getConfig().withTimepicker) {
            date.setHours(0);
            date.setMinutes(0);
        }

        date.setSeconds(0);
        date.setMilliseconds(0);
        return date;
    }

    getConfig(): BsDatepickerConfig {
        if (this.bsConfig) {
            return this.bsConfig;
        }

        return NgxBootstrapDatePickerConfigService.getDatepickerConfig();
    }

    
}
